import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BadgeCheck, ShieldX, Loader2, ArrowLeft } from "lucide-react";
import { api, assetUrl } from "@/lib/api";
import { extractFields, withSystem } from "@/lib/card";
import { CardSide } from "@/components/card/CardSide";
import { H, Eyebrow } from "@/components/site/SiteSections";

const pretty = (k) => k.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

export default function SiteCertificate({ base }) {
  const { rid } = useParams();
  const [res, setRes] = useState(null);
  useEffect(() => { window.scrollTo(0, 0); api.get(`/public/verify/${rid}`).then((r) => setRes(r.data)).catch(() => setRes(false)); }, [rid]);
  const fields = useMemo(() => (res ? extractFields(res.template) : []), [res]);
  const back = <Link to={base || "/"} className="inline-flex items-center gap-1 text-sm hover:underline" style={{ color: "var(--accent)" }} data-testid="site-cert-back"><ArrowLeft size={14} /> Back to showroom</Link>;

  if (res === null) return <div className="py-40 grid place-items-center"><Loader2 className="animate-spin" style={{ color: "var(--accent)" }} /></div>;
  if (!res)
    return (
      <div className="max-w-xl mx-auto px-6 py-28 text-center" data-testid="site-cert-not-found">
        <ShieldX size={44} className="mx-auto text-red-500" />
        <H className="mt-4">Certificate not found</H>
        <p className="text-sm mt-3" style={{ color: "var(--muted)" }}>This certificate could not be verified. Please contact the showroom.</p>
        <div className="mt-6">{back}</div>
      </div>
    );

  const { template: tpl, data, issued_at } = res;
  const photos = fields.filter((f) => f.type === "image" && data[f.key]);
  const texts = fields.filter((f) => f.type === "text" && String(data[f.key] ?? "").trim());
  const sys = withSystem({ id: res.record_id, data });
  const scale = Math.min(6, 340 / tpl.width);

  return (
    <div className="max-w-5xl mx-auto px-6 py-14 space-y-10" data-testid="site-certificate">
      {back}
      <div className="text-center rise">
        <Eyebrow>Certificate of authenticity</Eyebrow>
        <H data-testid="site-cert-title">{tpl.name}</H>
      </div>
      <div className="rounded-2xl border px-6 py-5 flex items-center gap-4 max-w-2xl mx-auto" style={{ borderColor: "#10B98155", background: "#10B98114" }} data-testid="site-cert-status">
        <BadgeCheck size={36} className="text-emerald-500 shrink-0" />
        <div>
          <p className="font-semibold text-emerald-500">Verified genuine</p>
          <p className="text-xs" style={{ color: "var(--muted)" }}>Issued {new Date(issued_at).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}{data.CERT_NO ? ` · ${data.CERT_NO}` : ""}</p>
        </div>
      </div>
      <div className={`grid gap-8 ${photos.length ? "md:grid-cols-[1fr_1.2fr]" : ""} items-start`}>
        {photos.length > 0 && (
          <div className="space-y-4">
            {photos.map((f) => <img key={f.key} src={assetUrl(data[f.key])} alt={pretty(f.key)} className="w-full aspect-square object-cover rounded-2xl border" style={{ borderColor: "var(--line)" }} data-testid={`site-cert-photo-${f.key}`} />)}
          </div>
        )}
        <dl className="rounded-2xl border divide-y" style={{ background: "var(--surface)", borderColor: "var(--line)" }} data-testid="site-cert-details">
          {texts.map((f) => (
            <div key={f.key} className="flex justify-between gap-4 px-5 py-3" style={{ borderColor: "var(--line)" }}>
              <dt className="text-xs uppercase tracking-wider" style={{ color: "var(--muted)" }}>{pretty(f.key)}</dt>
              <dd className="text-sm font-semibold text-right" data-testid={`site-cert-field-${f.key}`}>{String(data[f.key])}</dd>
            </div>
          ))}
        </dl>
      </div>
      <div className="flex flex-wrap justify-center gap-6">
        {["front", ...(tpl.double_sided ? ["back"] : [])].map((s) => (
          <div key={s} className="shadow-2xl rounded-xl"><CardSide tpl={tpl} side={s} data={sys} scale={scale} rounded testId={`site-cert-card-${s}`} /></div>
        ))}
      </div>
    </div>
  );
}
